// Contact.jsx - Phone first, form second. Most people who reach this page are
// worried about a package, and the fastest fix for that is a phone number in
// large type rather than a form they have to wait on.

import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, organisationSchema } from "../lib/seo.jsx";
import { useAsync, useForm } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import * as analytics from "../lib/analytics.js";
import { Button, Input, Textarea, Alert, Panel } from "../ui/kit.jsx";
import { Phone, Envelope, Message, CheckCircle } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { PayoutEnvelope } from "../ui/illustrations.jsx";
import { PageHead } from "./parts.jsx";

export default function Contact() {
  const { data: settings } = useAsync(() => publicApi.settings(), []);
  const company = settings?.company;
  const form = useForm({ name: "", email: "", phone: "", subject: "", message: "", website: "" });

  const submit = form.submit(async (values) => {
    const result = await publicApi.contact(values);
    analytics.trackContact({ topic: values.topic || undefined });
    form.setMessage({ kind: "success", text: result.message });
    form.setValues({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
    return result;
  });

  return (
    <>
      <Seo
        title="Contact us"
        path="/contact"
        description="Call International Gold and Silver on (401) 315-4653, Monday to Friday, 9am to 6pm Eastern, or send us a message and we will answer within one business day."
      />
      <JsonLd data={organisationSchema()} />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Contact", to: "/contact" }])} />

      <PageHead
        eyebrow="Contact"
        title="Talk to a person."
        lede="If your package is in the post and you are worried about it, call. That is always faster than anything you can type, and someone here will have the answer in front of them."
        art={() => <PayoutEnvelope className="max-w-[17rem] ml-auto" />}
      />

      <div className="shell pb-14 md:pb-24">
        <div className="grid gap-9 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <StaggerBoxed as="div" step={90} className="space-y-4">
            <Panel className="p-6">
              <Phone size={20} className="text-bronze mb-3" />
              <p className="eyebrow">Call us</p>
              <a href="tel:+1-401-315-4653" className="display display-md text-ink mt-1.5 block num link">
                {company?.phone || "(401) 315-4653"}
              </a>
              <p className="hint mt-2">{company?.supportHours || "Monday to Friday, 9am to 6pm Eastern"}</p>
            </Panel>

            <Panel className="p-6">
              <Message size={20} className="text-bronze mb-3" />
              <p className="eyebrow">Text us</p>
              <p className="text-[15px] text-ink-soft mt-1.5 leading-relaxed">
                If you already have an order, replying to any of our texts reaches the same people.
                It threads onto your order automatically.
              </p>
              <Link to="/track" className="btn btn-quiet btn-sm mt-4">Find my order</Link>
            </Panel>

            <Panel className="p-6">
              <Envelope size={20} className="text-bronze mb-3" />
              <p className="eyebrow">Email</p>
              <a href={`mailto:${company?.email || "orders@internationalgoldandsilver.com"}`} className="text-[15px] text-ink mt-1.5 block link break-all">
                {company?.email || "orders@internationalgoldandsilver.com"}
              </a>
              <p className="hint mt-2">Answered within one business day.</p>
            </Panel>

            {company?.address && (
              <Panel quiet className="p-6">
                <p className="eyebrow mb-2">Where packages go</p>
                <address className="text-[14.5px] text-ink-soft not-italic leading-relaxed">
                  {company.name}<br />
                  {company.address.line1}<br />
                  {company.address.line2 && <>{company.address.line2}<br /></>}
                  {company.address.city}, {company.address.state} {company.address.postal}
                </address>
                <p className="hint mt-3 leading-relaxed">
                  Do not post items here without a kit. The prepaid label inside the kit is what makes
                  your package insured.
                </p>
              </Panel>
            )}
          </StaggerBoxed>

          <Reveal from="right" delay={120}>
            <Panel className="p-5 sm:p-8">
              <h2 className="display display-md text-ink">Send us a message</h2>
              <p className="hint mt-2 mb-6">Everything is read by a person. No ticket numbers.</p>

              {form.message?.kind === "success" ? (
                <Alert tone="success" title="Message sent">
                  <p className="mt-1">{form.message.text}</p>
                </Alert>
              ) : (
                <form onSubmit={submit} noValidate className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Your name" required autoComplete="name" {...form.field("name")} />
                    <Input label="Email" type="email" required autoComplete="email" {...form.field("email")} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Phone" type="tel" autoComplete="tel" placeholder="Optional" {...form.field("phone")} />
                    <Input label="Order number" placeholder="IGS-XXXXXX, if you have one" {...form.field("subject")} />
                  </div>
                  <Textarea label="How can we help?" required rows={6} {...form.field("message")} />

                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute opacity-0 w-0 h-0 -z-10"
                    {...form.field("website")}
                  />

                  {form.message?.kind === "error" && <Alert tone="error">{form.message.text}</Alert>}

                  <Button type="submit" loading={form.busy} size="lg">Send message</Button>
                </form>
              )}
            </Panel>

            <div className="flex gap-3 mt-4 p-4 panel-quiet">
              <CheckCircle size={18} className="text-bronze shrink-0 mt-0.5" />
              <p className="hint leading-relaxed">
                Looking for the status of a package you already sent?{" "}
                <Link to="/track" className="link text-ink font-semibold">Open your tracking page</Link>{" "}
                instead. It updates live and shows the carrier scans as they happen.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
